export interface Point {
  x: number;
  y: number;
}

export interface BoardBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Tile gap as a fraction of the board's width and height; mirrors `.board { gap: 2% }` in style.css. */
export const BOARD_GAP = 0.02;

function geometry(bounds: BoardBounds, size: number) {
  const gapX = bounds.width * BOARD_GAP;
  const gapY = bounds.height * BOARD_GAP;
  const width = (bounds.width - gapX * (size - 1)) / size;
  const height = (bounds.height - gapY * (size - 1)) / size;
  return { width, height, stepX: width + gapX, stepY: height + gapY };
}

export function boardIndexAt(point: Point, bounds: BoardBounds, size: number): number {
  const { width, height, stepX, stepY } = geometry(bounds, size);
  if (width <= 0 || height <= 0) return -1;
  const x = point.x - bounds.left;
  const y = point.y - bounds.top;
  const column = Math.floor(x / stepX);
  const row = Math.floor(y / stepY);
  if (column < 0 || column >= size || row < 0 || row >= size) return -1;
  return x - column * stepX <= width && y - row * stepY <= height ? row * size + column : -1;
}

// Only new circle entries count: replaying the circle containing the start
// would undo a backtrack on the next sample. No interpolation beyond this segment.
export function crossedBoardTiles(from: Point, to: Point, bounds: BoardBounds, size: number): number[] {
  const { width, height, stepX, stepY } = geometry(bounds, size);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;
  if (!lengthSquared || width <= 0 || height <= 0) return [];
  const radiusSquared = (width * 0.45) ** 2;
  const hits: { index: number; time: number }[] = [];
  for (let index = 0; index < size * size; index++) {
    const x = from.x - (bounds.left + (index % size) * stepX + width / 2);
    const y = from.y - (bounds.top + Math.floor(index / size) * stepY + height / 2);
    const c = x * x + y * y - radiusSquared;
    if (c <= 0) continue;
    const b = x * dx + y * dy;
    const discriminant = b * b - lengthSquared * c;
    if (discriminant < 0) continue;
    const time = (-b - Math.sqrt(discriminant)) / lengthSquared;
    if (time >= 0 && time <= 1) hits.push({ index, time });
  }
  return hits.sort((a, b) => a.time - b.time || a.index - b.index).map(({ index }) => index);
}
